// @flow
import React from 'react';
import { get, kebabCase } from 'lodash';
import classNames from 'classnames';
import { type FieldProps } from 'formik';

import { getValidationStatus } from 'Components/frameworks/formik/inputsUtils';

import { ErrorNote } from './ErrorNote.jsx';
import { Label } from './Label.jsx';

type Props = {
  dataTestId?: string,
  hideErrorMessage: boolean,
  isDisabled?: boolean,
  isRequired?: boolean,
  label?: string,
  placeholder?: string,
  ...FieldProps,
};

export const Textarea = ({
  dataTestId,
  field,
  form,
  hideErrorMessage,
  isDisabled = false,
  isRequired = false,
  label,
  placeholder,
}: Props) => {
  // Textarea value defaults to empty string
  const value = get(form.values, field.name, '');
  const { isDefaultState, isErrored, isValidated } = getValidationStatus(
    { ...field, value },
    form,
    isDisabled,
  );
  const errorNoteId = `error-${kebabCase(field.name)}`;
  return (
    <React.Fragment>
      {!!label && (
        <Label htmlFor={field.name} isDisabled={isDisabled} isRequired={isRequired} label={label} />
      )}
      <textarea
        aria-describedby={errorNoteId}
        autoComplete="off"
        className={classNames(
          'w-full align-middle px-3 py-2 text-md sm:text-base text-gray-darker leading-normal border rounded-tiny outline-none transition-all',
          {
            'border-gray-medium focus:border-blue': isDefaultState,
            'border-emerald': isValidated,
            'border-red formik-errored': isErrored,
            'border-gray-lighter bg-gray-lighter cursor-not-allowed': isDisabled,
          },
        )}
        data-testid={dataTestId}
        disabled={isDisabled}
        id={field.name}
        {...(placeholder ? { placeholder } : null)}
        {...field}
        value={value}
      />
      {!hideErrorMessage && (
        <ErrorNote
          isErrored={isErrored}
          errorMessage={form.errors[field.name]}
          errorNoteId={errorNoteId}
        />
      )}
    </React.Fragment>
  );
};
